import { Input } from "@brg/input";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { AddButton, DropdownWrapper, NewCategoryButtonsContainer, NewCategoryInputsContainer, RowContainer, SmallLabel, TargetWrapper } from "../styles";
import { ProjectAddButton } from "shared/pages/projectEdit/ProjectAddButton";
import { useDispatch, useSelector } from "react-redux";
import { TagAdminSelector } from "shared/reducers/tagAdminSlice";
import { API, Tag } from "@brg/api";
import { fetchAllCategory } from "shared/actions/tagAdminActions";
import { useTranslation } from "react-i18next";
import { CurrentLangT, SUB_TYPES } from "./CategoryEdit";
import { Dropdown, DropdownListItem, FlexBox, MagicBox, useToasts } from "@brg/ui";
import { LayoutConst } from "core";
import styled from "styled-components";
import { CheckboxRoot } from "shared/pages/projectEdit/styles";
import {ReactComponent as CheckIcon} from "../../../../../shared/assets/check.svg";
import { createDefaultLangWithLocal, getLangTitle, TAG_ADMIN_OPERATION } from "../MainTagAdminPage";
import { LoaderSpinner } from "shared/modals/artzLegalsModal/LoaderAnimation";
import setTextByLang from "shared/helpers/setTextByLang";

type MagicBoxRowPropsT = {
    checked: boolean;
    label?: string;
    style?: any;
    onClick?: (ev: React.SyntheticEvent) => void;
    dark?: boolean;
};

export const Label = styled.div`
  color: ${({ theme }) => theme.projectEdit.btns.lang.biling.color};
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 17px;
  line-height: 22px;
  letter-spacing: -0.02em;
  white-space: pre-wrap;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 13px;
    line-height: 16px;
  }
`;

export const CheckboxField: FC<MagicBoxRowPropsT> = ({ onClick, checked, label, style, dark }) => (
    <MagicBox
      onClick={onClick}
      pointer
      align="center"
      gx={16}
    >
      <CheckboxRoot checked={checked} isProjectPage>
        {checked && (
          <>
            <CheckIcon />
          </>
        )}
        <input checked={checked} type="checkbox"/>
      </CheckboxRoot>
      <FlexBox width={'100%'} widthMobile={'100%'}>
        <Label>
          {label}
        </Label>
      </FlexBox>
    </MagicBox>
  );

  const ArrowWithText: FC<{ text: string, expanded?: boolean }> = ({text, expanded}) => {
   return (
    <TargetWrapper justify="space-between">
        <div className={'text'}>{text}</div>
        <svg className={'shevron-icon'} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 11.158L2.25981 5.22347C1.97161 4.92551 1.50435 4.92551 1.21615 5.22348C0.927948 5.52144 0.927951 6.00453 1.21615 6.30249L7.47817 12.7765C7.76637 13.0745 8.23363 13.0745 8.52183 12.7765L14.7838 6.30249C15.072 6.00453 15.0721 5.52144 14.7839 5.22348C14.4957 4.92551 14.0284 4.92551 13.7402 5.22347L8 11.158Z" fill="#909090"/>
        </svg>
    </TargetWrapper>
   )
  };

export const Categories: FC<{
    onSwitchCategories: (id: string, isExist?: boolean) => void;
    onCreateCategory: (name: CurrentLangT[]) => void;
    onLocalLangChange: (arr: any[], lang: string, callback: () => any[]) => void; 
    onUpdate: (t: any) => void,  
}> = ({ 
    onUpdate,
    onLocalLangChange,
    onCreateCategory,
    onSwitchCategories 
}) => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const toast = useToasts();
    const { categories, isLoading, currentPlatform  } = useSelector(TagAdminSelector);
    const [type, setType] = useState<SUB_TYPES | null>(null);
    const [subTitle, setSubTitle] = useState<CurrentLangT[]>(createDefaultLangWithLocal());
    const [time, setTime] = useState(Date.now());

    const isExist = (id: string) => {
        return !!currentPlatform?.categories?.find((i) => i?.tagId === id)
    }

    useEffect(() => {
        dispatch(fetchAllCategory())
    }, [time])

    const handleCreateCategory = async () => {
   
    const ruName = getLangTitle(subTitle, 'ru') || '';
    const enName = getLangTitle(subTitle, 'en') || '';

   
    if (ruName.length < 2 || enName.length < 2) {
        toast.error(t('main_admin_tag.error.min_length')); 
        return;
    }

    onCreateCategory && onCreateCategory(subTitle)
    setSubTitle(createDefaultLangWithLocal());
    setTime(Date.now());
    setType(null)
    onUpdate(Date.now())
    }

    const handleCancelCreate = () => {
        setType(null);
        setSubTitle(createDefaultLangWithLocal());
    }

    const onSubCategoryChange = useCallback((ev: any, lang: string) => {
        if (/^(|[a-zA-Zа-яА-ЯёЁ0-9][a-zA-Zа-яА-ЯёЁ0-9\s'-]*)$/.test(ev.target.value)) {
            //@ts-ignore
            setSubTitle(setTextByLang(subTitle, lang, ev.target.value))
        }
    } , [subTitle])

    const handleSwitchCategory = (id: string, flag?: boolean) => {
        onSwitchCategories(id, isExist(id))
        setTime(Date.now());
    }

    const computeCategories = useMemo(
        () => API.toLang(categories, i18n.language) 
    , [categories, i18n.language, time, isLoading]) as Tag<'Local'>[]

    const CategoryList = useMemo(() => (
        <DropdownWrapper onClick={() => setType(SUB_TYPES.add)}>
            <Dropdown
                hideArrow
                offsetY={18}
                maxHeight={384}
                target={
                    <ArrowWithText text={t('main_admin_tag.category_choose')} /> 
                }
            >
                {computeCategories?.map((tag) => {

                    return (
                        <DropdownListItem onClick={() => handleSwitchCategory(tag?.tagId, isExist(tag?.tagId))}>
                            {isLoading ? <LoaderSpinner />: <CheckboxField checked={isExist(tag?.tagId)} />}
                            <div>{tag?.name}</div>
                        </DropdownListItem>
                    )
                })}
            </Dropdown>
        </DropdownWrapper>
    ) , [
        computeCategories, 
        isExist, 
        onSwitchCategories, 
        isLoading,
        time
    ])

    const Buttons = useMemo(() => (
        <ProjectAddButton 
            disable={type === SUB_TYPES.sub} 
            onClick={() => setType(SUB_TYPES.sub)} 
            reverse 
            text={t('main_admin_tag.category_create_desk')}
        />
    ) , [type, i18n.language])

     const Inputs = useMemo(() => {
        if (type !== SUB_TYPES.sub) {
            return null;
        }

        const disable = subTitle?.some((i) => i?.name === '');

        return (
            <NewCategoryInputsContainer direction="column" gy={24} gyMobile={24}>
                <span className="label">{t('main_admin_tag.category_create_title')}</span>
                <FlexBox gy={16} gyMobile={16} direction="column" directionMobile="column">
                    <FlexBox direction="column">
                        <SmallLabel>{t('main_admin_tag.lang.category.ru')}</SmallLabel>
                        <Input.Text 
                            value={getLangTitle(subTitle, 'ru')}
                            onChange={(ev: any) => onSubCategoryChange(ev, 'ru')}
                            maxLength={75}
                        />
                        
                    </FlexBox>
                    <FlexBox direction="column">
                        <SmallLabel>{t('main_admin_tag.lang.category.en')}</SmallLabel>
                        <Input.Text 
                            value={getLangTitle(subTitle, 'en')}
                            onChange={(ev: any) => onSubCategoryChange(ev, 'en')}
                            maxLength={75}
                        />
                    </FlexBox>
                </FlexBox>
               
                <NewCategoryButtonsContainer gx={24} direction="row">
                    <AddButton style={{ marginLeft: 0 }} onClick={handleCancelCreate}>{t('main_admin_tag.buttons.cancel')}</AddButton>
                    <AddButton filled disable={disable} onClick={handleCreateCategory}>{t('main_admin_tag.buttons.add')}</AddButton>
                </NewCategoryButtonsContainer>
            </NewCategoryInputsContainer>
        )
    } , [subTitle, type, handleCreateCategory, onSubCategoryChange, time])

    return (
        <RowContainer gy={32} offsetBottom={40}>
            <FlexBox direction="column" directionMobile="column">
                <span className={'label'}>
                    {t('main_admin_tag.tag_category_title')}
                </span>
                <span className="description">
                    {t('main_admin_tag.tag_category_desk')}
                </span>
            </FlexBox>
            {CategoryList}
            {Buttons}
            {Inputs}
        </RowContainer>
    )
}import { Input } from "@brg/input";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { AddButton, DropdownWrapper, NewCategoryButtonsContainer, NewCategoryInputsContainer, RowContainer, SmallLabel, TargetWrapper } from "../styles";
import { ProjectAddButton } from "shared/pages/projectEdit/ProjectAddButton";
import { useDispatch, useSelector } from "react-redux";
import { TagAdminSelector } from "shared/reducers/tagAdminSlice";
import { API, Tag } from "@brg/api";
import { fetchAllCategory } from "shared/actions/tagAdminActions";
import { useTranslation } from "react-i18next";
import { CurrentLangT, SUB_TYPES } from "./CategoryEdit";
import { Dropdown, DropdownListItem, FlexBox, MagicBox, useToasts } from "@brg/ui";
import { LayoutConst } from "core";
import styled from "styled-components";
import { CheckboxRoot } from "shared/pages/projectEdit/styles";
import {ReactComponent as CheckIcon} from "../../../../../shared/assets/check.svg";
import { createDefaultLangWithLocal, getLangTitle, TAG_ADMIN_OPERATION } from "../MainTagAdminPage";
import { LoaderSpinner } from "shared/modals/artzLegalsModal/LoaderAnimation";
import setTextByLang from "shared/helpers/setTextByLang";

type MagicBoxRowPropsT = {
    checked: boolean;
    label?: string;
    style?: any;
    onClick?: (ev: React.SyntheticEvent) => void;
    dark?: boolean;
};

export const Label = styled.div`
  color: ${({ theme }) => theme.projectEdit.btns.lang.biling.color};
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 17px;
  line-height: 22px;
  letter-spacing: -0.02em;
  white-space: pre-wrap;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 13px;
    line-height: 16px;
  }
`;

export const CheckboxField: FC<MagicBoxRowPropsT> = ({ onClick, checked, label, style, dark }) => (
    <MagicBox
      onClick={onClick}
      pointer
      align="center"
      gx={16}
    >
      <CheckboxRoot checked={checked} isProjectPage>
        {checked && (
          <>
            <CheckIcon />
          </>
        )}
        <input checked={checked} type="checkbox"/>
      </CheckboxRoot>
      <FlexBox width={'100%'} widthMobile={'100%'}>
        <Label>
          {label}
        </Label>
      </FlexBox>
    </MagicBox>
  );

  const ArrowWithText: FC<{ text: string, expanded?: boolean }> = ({text, expanded}) => {
   return (
    <TargetWrapper justify="space-between">
        <div className={'text'}>{text}</div>
        <svg className={'shevron-icon'} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 11.158L2.25981 5.22347C1.97161 4.92551 1.50435 4.92551 1.21615 5.22348C0.927948 5.52144 0.927951 6.00453 1.21615 6.30249L7.47817 12.7765C7.76637 13.0745 8.23363 13.0745 8.52183 12.7765L14.7838 6.30249C15.072 6.00453 15.0721 5.52144 14.7839 5.22348C14.4957 4.92551 14.0284 4.92551 13.7402 5.22347L8 11.158Z" fill="#909090"/>
        </svg>
    </TargetWrapper>
   )
  };

export const Categories: FC<{
    onSwitchCategories: (id: string, isExist?: boolean) => void;
    onCreateCategory: (name: CurrentLangT[]) => void;
    onLocalLangChange: (arr: any[], lang: string, callback: () => any[]) => void; 
    onUpdate: (t: any) => void,  
}> = ({ 
    onUpdate,
    onLocalLangChange,
    onCreateCategory,
    onSwitchCategories 
}) => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const toast = useToasts();
    const { categories, isLoading, currentPlatform  } = useSelector(TagAdminSelector);
    const [type, setType] = useState<SUB_TYPES | null>(null);
    const [subTitle, setSubTitle] = useState<CurrentLangT[]>(createDefaultLangWithLocal());
    const [time, setTime] = useState(Date.now());

    const isExist = (id: string) => {
        return !!currentPlatform?.categories?.find((i) => i?.tagId === id)
    }

    useEffect(() => {
        dispatch(fetchAllCategory())
    }, [time])

    const handleCreateCategory = async () => {
   
    const ruName = getLangTitle(subTitle, 'ru') || '';
    const enName = getLangTitle(subTitle, 'en') || '';

   
    if (ruName.length < 2 || enName.length < 2) {
        toast.error(t('main_admin_tag.error.min_length')); 
        return;
    }

    onCreateCategory && onCreateCategory(subTitle)
    setSubTitle(createDefaultLangWithLocal());
    setTime(Date.now());
    setType(null)
    onUpdate(Date.now())
    }

    const handleCancelCreate = () => {
        setType(null);
        setSubTitle(createDefaultLangWithLocal());
    }

    const onSubCategoryChange = useCallback((ev: any, lang: string) => {
        if (/^(|[a-zA-Zа-яА-ЯёЁ0-9][a-zA-Zа-яА-ЯёЁ0-9\s'-]*)$/.test(ev.target.value)) {
            //@ts-ignore
            setSubTitle(setTextByLang(subTitle, lang, ev.target.value))
        }
    } , [subTitle])

    const handleSwitchCategory = (id: string, flag?: boolean) => {
        onSwitchCategories(id, isExist(id))
        setTime(Date.now());
    }

    const computeCategories = useMemo(
        () => API.toLang(categories, i18n.language) 
    , [categories, i18n.language, time, isLoading]) as Tag<'Local'>[]

    const CategoryList = useMemo(() => (
        <DropdownWrapper onClick={() => setType(SUB_TYPES.add)}>
            <Dropdown
                hideArrow
                offsetY={18}
                maxHeight={384}
                target={
                    <ArrowWithText text={t('main_admin_tag.category_choose')} /> 
                }
            >
                {computeCategories?.map((tag) => {

                    return (
                        <DropdownListItem onClick={() => handleSwitchCategory(tag?.tagId, isExist(tag?.tagId))}>
                            {isLoading ? <LoaderSpinner />: <CheckboxField checked={isExist(tag?.tagId)} />}
                            <div>{tag?.name}</div>
                        </DropdownListItem>
                    )
                })}
            </Dropdown>
        </DropdownWrapper>
    ) , [
        computeCategories, 
        isExist, 
        onSwitchCategories, 
        isLoading,
        time
    ])

    const Buttons = useMemo(() => (
        <ProjectAddButton 
            disable={type === SUB_TYPES.sub} 
            onClick={() => setType(SUB_TYPES.sub)} 
            reverse 
            text={t('main_admin_tag.category_create_desk')}
        />
    ) , [type, i18n.language])

     const Inputs = useMemo(() => {
        if (type !== SUB_TYPES.sub) {
            return null;
        }

        const disable = subTitle?.some((i) => i?.name === '');

        return (
            <NewCategoryInputsContainer direction="column" gy={24} gyMobile={24}>
                <span className="label">{t('main_admin_tag.category_create_title')}</span>
                <FlexBox gy={16} gyMobile={16} direction="column" directionMobile="column">
                    <FlexBox direction="column">
                        <SmallLabel>{t('main_admin_tag.lang.category.ru')}</SmallLabel>
                        <Input.Text 
                            value={getLangTitle(subTitle, 'ru')}
                            onChange={(ev: any) => onSubCategoryChange(ev, 'ru')}
                            maxLength={75}
                        />
                        
                    </FlexBox>
                    <FlexBox direction="column">
                        <SmallLabel>{t('main_admin_tag.lang.category.en')}</SmallLabel>
                        <Input.Text 
                            value={getLangTitle(subTitle, 'en')}
                            onChange={(ev: any) => onSubCategoryChange(ev, 'en')}
                            maxLength={75}
                        />
                    </FlexBox>
                </FlexBox>
               
                <NewCategoryButtonsContainer gx={24} direction="row">
                    <AddButton style={{ marginLeft: 0 }} onClick={handleCancelCreate}>{t('main_admin_tag.buttons.cancel')}</AddButton>
                    <AddButton filled disable={disable} onClick={handleCreateCategory}>{t('main_admin_tag.buttons.add')}</AddButton>
                </NewCategoryButtonsContainer>
            </NewCategoryInputsContainer>
        )
    } , [subTitle, type, handleCreateCategory, onSubCategoryChange, time])

    return (
        <RowContainer gy={32} offsetBottom={40}>
            <FlexBox direction="column" directionMobile="column">
                <span className={'label'}>
                    {t('main_admin_tag.tag_category_title')}
                </span>
                <span className="description">
                    {t('main_admin_tag.tag_category_desk')}
                </span>
            </FlexBox>
            {CategoryList}
            {Buttons}
            {Inputs}
        </RowContainer>
    )
}